import { useState, useCallback, useRef, useMemo, memo } from 'react';
import styled from 'styled-components';
import { Line } from 'react-chartjs-2';
import {
	Chart as ChartJS,
	LineElement,
	PointElement,
	LinearScale,
	Filler,
	Tooltip,
} from 'chart.js';
import { LuChevronDown } from 'react-icons/lu';
import { getCountingMethodConfig } from './countingMethodConfig';

ChartJS.register(LineElement, PointElement, LinearScale, Filler, Tooltip);

const ChartArea = styled.div`
	background: rgba(152, 152, 152, 0.03);
	border: 1px solid ${({ theme }) => theme.colors.border};
	border-radius: 8px;
	padding: 12px 16px 8px;
	flex-shrink: 0;
	transition:
		min-height 0.3s,
		max-height 0.3s,
		padding 0.3s;
	min-height: ${({ $collapsed }) => ($collapsed ? '0' : '200px')};
	max-height: ${({ $collapsed }) => ($collapsed ? '42px' : '300px')};
	overflow: hidden;

	${({ $collapsed }) => $collapsed && `padding-bottom: 12px;`}
`;

const ChartHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 8px;
	cursor: pointer;
	user-select: none;
`;

const HeaderLeft = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const Chevron = styled.span`
	font-size: 12px;
	color: ${({ theme }) => theme.colors.textAccent};
	transition: transform 0.25s;
	display: flex;
	align-items: center;
	transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const Title = styled.h4`
	font-size: 12px;
	font-weight: 600;
	margin: 0;
`;

const _ExportButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 3px 10px;
	border-radius: 5px;
	font-family: inherit;
	font-size: 11px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.2s;
	border: 1px solid ${({ theme }) => theme.colors.border};
	background: ${({ theme }) => theme.colors.backgroundAccent};
	color: ${({ theme }) => theme.colors.textColor};

	&:hover {
		border-color: ${({ theme }) => theme.colors.green};
		color: ${({ theme }) => theme.colors.green};
	}
`;

const ChartBody = styled.div`
	opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
	pointer-events: ${({ $collapsed }) => ($collapsed ? 'none' : 'auto')};
	height: ${({ $collapsed }) => ($collapsed ? 0 : 'auto')};
	overflow: hidden;
	transition: opacity 0.3s;
`;

const chartContainerStyle = { height: '170px' };

const formatSec = sec => {
	const m = Math.floor(sec / 60);
	const s = (sec % 60).toFixed(3);
	return `${String(m).padStart(2, '0')}:${s.padStart(6, '0')}`;
};

const ChartContent = memo(({ data, peakPoint, onPointClick, countingMethod }) => {
	const onPointClickRef = useRef(onPointClick);
	onPointClickRef.current = onPointClick;

	const methodConfig = useMemo(() => getCountingMethodConfig(countingMethod), [countingMethod]);

	const peakIndex = useMemo(() => {
		if (!data || !peakPoint) return -1;
		const { frameField } = methodConfig;
		return data.findIndex(
			d =>
				d.timestamp_sec === peakPoint.timestamp_sec &&
				d[frameField] === peakPoint[frameField]
		);
	}, [data, peakPoint, methodConfig]);

	const chartConfig = useMemo(() => {
		if (!data || data.length === 0) return null;

		const { frameField } = methodConfig;
		const pointRadius = data.map((_, i) => (i === peakIndex ? 6 : 0));
		const pointBorderWidth = data.map((_, i) => (i === peakIndex ? 2 : 0));

		return {
			labels: data.map(d => d.timestamp_sec),
			datasets: [
				{
					data: data.map(d => d[frameField]),
					borderColor: '#20AC6B',
					backgroundColor: 'rgba(32, 172, 107, 0.1)',
					fill: true,
					tension: 0.3,
					pointRadius,
					pointBorderWidth,
					pointBackgroundColor: data.map((_, i) =>
						i === peakIndex ? '#fff' : 'transparent'
					),
					pointBorderColor: data.map((_, i) =>
						i === peakIndex ? '#20AC6B' : 'transparent'
					),
					pointHoverRadius: 5,
					pointHoverBackgroundColor: '#20AC6B',
					borderWidth: 2,
				},
			],
		};
	}, [data, peakIndex, methodConfig]);

	const options = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			interaction: {
				mode: 'index',
				intersect: false,
			},
			onClick: (_event, elements) => {
				if (elements.length > 0) {
					onPointClickRef.current?.(data[elements[0].index]);
				}
			},
			plugins: {
				legend: { display: false },
				tooltip: {
					caretPadding: 15,
					displayColors: false,
					callbacks: {
						title: items => {
							const d = data[items[0].dataIndex];
							const isPeak = items[0].dataIndex === peakIndex;
							return isPeak
								? `⬆ Peak — Frame No.: ${d.frame_id}`
								: `Frame No.: ${d.frame_id}`;
						},
						label: item => {
							const d = data[item.dataIndex];
							const { frameField, tooltipFrameLabel } = methodConfig;
							return [
								`Time: ${formatSec(d.timestamp_sec)}`,
								`${tooltipFrameLabel}: ${d[frameField]}`,
							];
						},
					},
				},
			},
			scales: {
				x: {
					type: 'linear',
					ticks: {
						color: '#888',
						font: { size: 10 },
						maxRotation: 0,
						callback: val => formatSec(val),
						maxTicksLimit: 10,
					},
					grid: { color: 'rgba(51,51,51,0.3)' },
				},
				y: {
					ticks: { color: '#888', font: { size: 10 } },
					grid: { color: 'rgba(51,51,51,0.3)' },
					grace: '5%',
				},
			},
		}),
		[data, peakIndex, methodConfig]
	);

	if (!chartConfig) return null;

	return (
		<div style={chartContainerStyle}>
			<Line data={chartConfig} options={options} />
		</div>
	);
});

ChartContent.displayName = 'ChartContent';

const DetectionChart = memo(({ data, peakPoint, onPointClick, countingMethod }) => {
	const [collapsed, setCollapsed] = useState(false);
	const methodConfig = useMemo(() => getCountingMethodConfig(countingMethod), [countingMethod]);

	const toggle = useCallback(() => setCollapsed(prev => !prev), []);

	return (
		<ChartArea $collapsed={collapsed}>
			<ChartHeader onClick={toggle}>
				<HeaderLeft>
					<Chevron $open={!collapsed}>
						<LuChevronDown />
					</Chevron>
					<Title>{methodConfig.chartTitle}</Title>
				</HeaderLeft>
				{/*<ExportButton*/}
				{/*	onClick={(e) => {*/}
				{/*		e.stopPropagation();*/}
				{/*		onExportCsv?.();*/}
				{/*	}}*/}
				{/*>*/}
				{/*	Export CSV*/}
				{/*</ExportButton>*/}
			</ChartHeader>
			<ChartBody $collapsed={collapsed}>
				<ChartContent
					data={data}
					peakPoint={peakPoint}
					onPointClick={onPointClick}
					countingMethod={countingMethod}
				/>
			</ChartBody>
		</ChartArea>
	);
});

DetectionChart.displayName = 'DetectionChart';

export default DetectionChart;
